import { useState } from "react";

interface CreateCheckoutSessionInput {
  organizationId: string;
  priceId: string;
  successUrl: string;
  cancelUrl: string;
}

interface CreateCheckoutSessionReturn {
  createCheckoutSession: (
    input: CreateCheckoutSessionInput
  ) => Promise<string | null>;
  loading: boolean;
  error: string | null;
}

const useCreateCheckoutSession = (): CreateCheckoutSessionReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCheckoutSession = async (
    input: CreateCheckoutSessionInput
  ): Promise<string | null> => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          permissionKey: "create_checkout",
        },
        body: JSON.stringify({
          query: `
            mutation CreateCheckoutSession($input: CreateCheckoutSessionInput!) {
              createCheckoutSession(input: $input) {
                url
              }
            }
          `,
          variables: {
            input,
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create checkout session");
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(data.errors[0].message);
      }

      return data.data.createCheckoutSession.url;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to create checkout session";
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    createCheckoutSession,
    loading,
    error,
  };
};

export default useCreateCheckoutSession;
